import React, { useEffect, useState } from 'react'
import { removeFromQueue } from '../utils/queueStorage'
import { useAuth } from '../../auth/AuthContext'

const STATUS_COLS = [
  { key: 'queued',       label: 'Queue',        color: '#9a9a9a' },
  { key: 'evaluating',   label: 'Evaluating',   color: '#e8c47f' },
  { key: 'resume_ready', label: 'Resume Ready', color: '#58a6ff' },
  { key: 'applied',      label: 'Applied',      color: '#8888e8' },
  { key: 'interviewing', label: 'Interview',    color: '#a088e8' },
  { key: 'offer',        label: 'Offer',        color: '#7fc97f' },
  { key: 'rejected',     label: 'Rejected',     color: '#ff8888' },
]

const SCORE_COLOR = { A: '#7fc97f', B: '#58a6ff', C: '#e8c47f', D: '#e8a07f', F: '#ff8888' }

function daysSince(ts) {
  const d = Math.floor((Date.now() - new Date(ts)) / 86400000)
  if (d === 0) return 'Today'
  if (d === 1) return '1d ago'
  return `${d}d ago`
}

function ScoreBadge({ letter }) {
  if (!letter) return null
  return (
    <span style={{
      background: SCORE_COLOR[letter] || '#555',
      color: '#000',
      fontWeight: 700,
      fontSize: '0.7rem',
      padding: '1px 6px',
      borderRadius: 4,
      letterSpacing: 1,
    }}>{letter}</span>
  )
}

export function PipelineView({ queue, onQueueChange, onEvaluate, onTailor, onViewEval }) {
  const { token } = useAuth()
  const [pipeline, setPipeline] = useState([])
  const [loading, setLoading] = useState(false)

  // Load backend pipeline if authenticated
  useEffect(() => {
    if (!token) return
    setLoading(true)
    fetch('/api/ark/pipeline', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(rows => setPipeline(rows))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const moveStatus = async (id, status) => {
    if (!token) return
    await fetch(`/api/ark/pipeline/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    })
    setPipeline(p => p.map(e => e.id === id ? { ...e, status } : e))
  }

  const deleteEntry = async (id) => {
    if (!token) return
    await fetch(`/api/ark/pipeline/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    setPipeline(p => p.filter(e => e.id !== id))
  }

  // Add queued localStorage jobs to backend pipeline
  const promoteQueueItem = async (job) => {
    if (!token) { onEvaluate(job); return }
    const res = await fetch('/api/ark/pipeline', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company_name: job.company_name,
        job_title: job.job_title,
        job_url: job.job_url || null,
        status: 'evaluating',
      }),
    })
    if (res.ok) {
      const entry = await res.json()
      setPipeline(p => [entry, ...p])
      const next = removeFromQueue(job._key)
      onQueueChange(next)
      onEvaluate({ ...job, pipeline_id: entry.id })
    }
  }

  const grouped = STATUS_COLS.reduce((acc, col) => {
    acc[col.key] = pipeline.filter(e => e.status === col.key)
    return acc
  }, {})

  return (
    <div className="ark-pipeline">

      {/* ── Local queue (not yet evaluated) ── */}
      {queue.length > 0 && (
        <section className="ark-pipeline-queue-section">
          <div className="ark-home-section-header">
            <h2 className="ark-home-section-title">Incoming Queue</h2>
            <span className="ark-home-section-count">{queue.length}</span>
          </div>
          <p className="ark-pipeline-hint">
            Jobs from Open Roles. Click <strong>Evaluate</strong> to score fit and generate a report.
          </p>
          <ul className="ark-queue-list">
            {queue.map(job => (
              <li key={job._key} className="ark-queue-item">
                <div className="ark-queue-item-info">
                  <span className="ark-queue-title">{job.job_title}</span>
                  <span className="ark-queue-company">{job.company_name}</span>
                </div>
                <div className="ark-queue-item-actions">
                  {job.job_url && (
                    <a href={job.job_url} target="_blank" rel="noopener noreferrer" className="ark-queue-view-link">View →</a>
                  )}
                  <button className="ark-eval-btn" onClick={() => promoteQueueItem(job)}>
                    Evaluate
                  </button>
                  <button className="ark-queue-tailor-btn" onClick={() => onTailor(job)}>
                    Tailor resume
                  </button>
                  <button className="ark-queue-remove-btn" onClick={() => onQueueChange(removeFromQueue(job._key))}>×</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Pipeline columns ── */}
      {token ? (
        loading ? (
          <div className="ark-home-empty">Loading pipeline…</div>
        ) : (
          <div className="ark-pipeline-board">
            {STATUS_COLS.filter(c => c.key !== 'queued').map(col => (
              <div key={col.key} className="ark-pipeline-col">
                <div className="ark-pipeline-col-header" style={{ borderColor: col.color }}>
                  <span style={{ color: col.color }}>{col.label}</span>
                  <span className="ark-pipeline-col-count">{grouped[col.key]?.length || 0}</span>
                </div>
                {(grouped[col.key] || []).map(entry => (
                  <div key={entry.id} className="ark-pipeline-card">
                    <div className="ark-pipeline-card-top">
                      <span className="ark-pipeline-card-title">{entry.job_title}</span>
                      {entry.score_letter && <ScoreBadge letter={entry.score_letter} />}
                    </div>
                    <div className="ark-pipeline-card-company">{entry.company_name}</div>
                    <div className="ark-pipeline-card-meta">{daysSince(entry.created_at)}</div>
                    <div className="ark-pipeline-card-actions">
                      {entry.evaluation_json && (
                        <button className="ark-pipeline-card-btn" onClick={() => onViewEval(entry)}>Report</button>
                      )}
                      {!entry.evaluation_json && (
                        <button className="ark-eval-btn ark-eval-btn--sm" onClick={() => onEvaluate(entry)}>Evaluate</button>
                      )}
                      {entry.status === 'resume_ready' || entry.status === 'evaluating' ? (
                        <button className="ark-queue-tailor-btn" onClick={() => onTailor(entry)}>Resume</button>
                      ) : null}
                      <select
                        className="ark-pipeline-status-select"
                        value={entry.status}
                        onChange={e => moveStatus(entry.id, e.target.value)}
                      >
                        {STATUS_COLS.filter(c => c.key !== 'queued').map(c => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                      <button className="ark-queue-remove-btn" onClick={() => deleteEntry(entry.id)}>×</button>
                    </div>
                  </div>
                ))}
                {(grouped[col.key] || []).length === 0 && (
                  <div className="ark-pipeline-col-empty">—</div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="ark-home-empty" style={{ marginTop: 24 }}>
          <a href="/account" className="ark-home-link">Sign in</a> to save your pipeline across sessions.
        </div>
      )}
    </div>
  )
}
